import React from "react";

import { FaX, FaCheck } from "react-icons/fa6";

import { formatDate, timeToString } from "../../../../utils/datetime";
import { getUserDisplayName } from "../../../../utils/business";

import { CHIP_COLORS, CURRENT_ROW_TINT, STAGE_PILL_SIZE } from "./constants";
import { getDispatchStageLogPositionClass } from "../dispatchOverlayAlign";
import {
  getBroadcastMessagePreview,
  getCurrentLifecycleStage,
  getLastLifecycleIndex,
  getStagePillClass,
} from "./helpers";

/**
 * Compact Daily Review checklist under a Reviewed history row.
 *
 * @param {Object} props
 * @param {boolean} props.accidentsReported Whether accidents were confirmed.
 * @param {boolean} props.changeOrdersIssued Whether change orders were confirmed.
 * @param {boolean} props.timeMaterialReported Whether T&M was confirmed.
 * @returns {JSX.Element}
 */
function ReviewChecklist({
  accidentsReported,
  changeOrdersIssued,
  timeMaterialReported,
}) {
  const items = [
    { met: accidentsReported, label: "All Accidents reported" },
    { met: changeOrdersIssued, label: "All Change orders issued" },
    { met: timeMaterialReported, label: "All T&M reported" },
  ];
  return (
    <ul className="mt-0.5 flex flex-wrap gap-x-2.5 gap-y-0 text-[10px] leading-none text-gray-600">
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-1">
          {item.met ? (
            <FaCheck size={9} className="shrink-0 text-green-700" />
          ) : (
            <FaX size={9} className="shrink-0 text-red-600" />
          )}
          <span>{item.label}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Badge showing the current dispatch lifecycle stage for one day (Draft,
 * Dispatched, Reviewed, or Modified). Hover reveals history when any stage
 * rows exist, including history-only Broadcast on a Draft day.
 *
 * @param {Object} props
 * @param {string} [props.className] Additional classes for the badge.
 * @param {Object} props.dayDetails Day row from dispatch day details (`dispatch_stage` history).
 * @param {boolean} [props.isFirstDay] When true, anchors the hover log to the left.
 * @param {boolean} [props.isLastDay] When true, anchors the hover log to the right.
 * @returns {JSX.Element|null}
 */
const DispatchStage = ({ className, dayDetails, isFirstDay = false, isLastDay = false }) => {
  if (!dayDetails) {
    return null;
  }

  const stageDetails = dayDetails.dispatch_stage || [];
  const stage = getCurrentLifecycleStage(stageDetails);
  const lastLifecycleIndex = getLastLifecycleIndex(stageDetails);
  // Draft with no rows stays a static label; Draft with Broadcast (or other) history
  // still opens the hover log even though the pill itself remains Draft.
  const hasHistory = stageDetails.length > 0;
  const color = getStagePillClass(stage, hasHistory);
  const stageLogEdge = isFirstDay ? "start" : isLastDay ? "end" : "middle";
  const stageLogPositionClass = getDispatchStageLogPositionClass(stageLogEdge);

  return (
    <div className="group/state relative flex cursor-default flex-row">
      <div
        className={`${className} ${STAGE_PILL_SIZE} ${color} ${hasHistory ? "peer/state" : ""}`}
      >
        {stage}
      </div>

      {hasHistory ? (
        <div
          className={`absolute top-full z-[100] mt-1 min-w-[24rem] max-w-[30rem] scale-0 rounded-lg border border-gray-300 bg-white px-1.5 py-1 text-left text-xs text-gray-700 shadow-lg transition-transform ease-in-out peer-hover/state:scale-100 group-hover/state:scale-100 hover:scale-100 ${stageLogPositionClass}`}
        >
          {stageDetails.map((stageDetail, index) => {
            const isCurrent = index === lastLifecycleIndex;
            const actor = stageDetail.user
              ? getUserDisplayName(stageDetail.user)
              : stageDetail.stage === "Dispatched"
                ? "RSApp Auto-Complete Dispatch"
                : "RSApp Auto-Review";
            const time = timeToString(stageDetail.time);
            return (
              <div
                key={`${stageDetail.stage}-${stageDetail.date}-${stageDetail.time}-${index}`}
                className={`px-1.5 py-1.5 ${index > 0 ? "border-t border-gray-200" : ""} ${
                  isCurrent
                    ? `rounded-md ${CURRENT_ROW_TINT[stageDetail.stage] || "bg-gray-50 ring-1 ring-gray-200"}`
                    : ""
                }`}
              >
                <div className="flex min-w-0 items-center gap-1.5">
                  <span
                    className={`${STAGE_PILL_SIZE} ${
                      CHIP_COLORS[stageDetail.stage] || CHIP_COLORS.Draft
                    }`}
                  >
                    {stageDetail.stage}
                  </span>
                  <span className="min-w-0 truncate text-xs leading-4 text-gray-500">
                    {formatDate(stageDetail.date, "MM/DD/YY")}, {time} {actor}
                  </span>
                  {isCurrent ? (
                    <span className="ml-auto shrink-0 text-[10px] font-semibold uppercase leading-4 tracking-wide text-gray-500">
                      Current
                    </span>
                  ) : null}
                </div>
                {stageDetail.stage === "Reviewed" ? (
                  <ReviewChecklist
                    accidentsReported={stageDetail.accidents_reported}
                    changeOrdersIssued={stageDetail.change_orders_issued}
                    timeMaterialReported={stageDetail.time_material_reported}
                  />
                ) : null}
                {stageDetail.stage === "Broadcast" ? (
                  // additional_info.message is the full SMS body; userIds are stored for later use.
                  <p className="mt-0.5 text-[11px] leading-none text-gray-500">
                    {getBroadcastMessagePreview(stageDetail.additional_info)}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default DispatchStage;
